import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Reveal, Chapter } from "./Reveal";
import { GoogleMark } from "./Header";
import { STORES } from "../../data/stores";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", store_location: "" };

export default function SignupForm({ user, onLogin }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email }));
    }
  }, [user]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const showValidation = (detail) => {
    let msg;
    if (typeof detail === "string") {
      msg = detail;
    } else if (Array.isArray(detail) && detail.length) {
      const field = detail[0]?.loc?.filter((l) => l !== "body")?.[0];
      const friendly = {
        name: "Please enter your full name.",
        phone: "Enter a valid contact number, e.g. +91 98765 43210.",
        email: "Enter a valid email address.",
        store_location: "Please choose your store location.",
      };
      msg = friendly[field] || "Please check your details and try again.";
    } else {
      msg = "Please check your details and try again.";
    }
    toast.error(msg, { duration: 6000 });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const endpoints = [`${process.env.PUBLIC_URL}/signup.php`, `${API}/signups`];
    let data = null;
    let validationError = null;
    for (const url of endpoints) {
      try {
        const res = await axios.post(url, form, { timeout: 20000 });
        if (res.data?.status !== "success") throw new Error("unexpected_response");
        data = res.data;
        break;
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response && detail && detail !== "upstream_unreachable") {
          validationError = detail;
          break;
        }
      }
    }
    if (data) {
      setReference(data.id);
      toast.success("Offer registered! Our team will reach out shortly.");
    } else if (validationError) {
      showValidation(validationError);
    } else {
      toast.error(
        "Can't reach our servers right now. Please try again in a minute, or call us at +91 63664 37841.",
        { duration: 8000 }
      );
    }
    setLoading(false);
  };

  return (
    <section id="claim" data-testid="claim-section" className="relative border-t border-white/10 py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-2">
        <Reveal>
          <Chapter number="03" title="Claim the Offer" />
          <h2 className="font-display text-4xl uppercase tracking-tight text-white sm:text-5xl">
            Lock in <span className="text-[#FF3B30]">your reward</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
            Register online in under a minute. Walk into your chosen Tyre Empire store, buy 4 car
            tyres, and drive out with your reward.
          </p>
          <ul className="mt-10 space-y-5">
            {[
              "Register online — the offer is exclusive to online signups",
              "Pick the store where you want to claim the offer",
              "Our team calls you to schedule your fitting",
            ].map((point, i) => (
              <li key={point} className="flex items-start gap-4">
                <span className="font-display text-xl text-[#FFCC00]">0{i + 1}</span>
                <span className="text-sm text-white/70 md:text-base">{point}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border border-white/10 bg-[#121212] p-8 md:p-10">
            {user ? (
              <div
                data-testid="signed-in-banner"
                className="mb-6 flex items-center gap-3 border border-[#FFCC00]/30 bg-[#FFCC00]/5 px-4 py-3"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 border border-white/20 object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center border border-white/20 font-heading text-xs font-bold text-[#FFCC00]">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                )}
                <p className="text-sm text-white/70">
                  Signed in as <span className="font-semibold text-white">{user.email}</span>
                </p>
              </div>
            ) : (
              <button
                type="button"
                data-testid="form-google-signin-button"
                onClick={onLogin}
                className="mb-6 flex w-full items-center justify-center gap-3 border border-white/20 px-4 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-white/80 transition-colors duration-300 hover:border-white hover:text-white"
              >
                <GoogleMark /> Sign in with Google to autofill
              </button>
            )}
            {reference ? (
              <div data-testid="signup-success-message" className="flex flex-col items-start gap-6 py-8">
                <BadgeCheck className="h-14 w-14 text-[#FFCC00]" aria-hidden />
                <h3 className="font-display text-3xl uppercase text-white">You're registered!</h3>
                <p className="text-sm leading-relaxed text-white/60 md:text-base">
                  Your offer claim is locked in for our{" "}
                  <span className="font-semibold text-white">{form.store_location}</span> store. Our
                  team will call you on <span className="font-semibold text-white">{form.phone}</span>{" "}
                  to schedule your visit. A confirmation email with your reference ID is on its way
                  to <span className="font-semibold text-white">{form.email}</span>.
                </p>
                <p className="font-heading text-xs uppercase tracking-[0.25em] text-white/40">
                  Reference · {reference.slice(0, 8).toUpperCase()}
                </p>
                <button
                  data-testid="signup-again-button"
                  onClick={() => { setForm(EMPTY); setReference(null); }}
                  className="mt-2 border border-white/20 px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                >
                  Register another
                </button>
              </div>
            ) : (
              <form data-testid="signup-form" onSubmit={onSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="signup-name" className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-white/60">
                    Full Name
                  </Label>
                  <Input
                    id="signup-name"
                    data-testid="signup-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Ravi Kumar"
                    className="h-12 rounded-none border-white/20 bg-black text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#FF3B30]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-phone" className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-white/60">
                    Contact Number
                  </Label>
                  <Input
                    id="signup-phone"
                    data-testid="signup-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 98XXX XXXXX"
                    className="h-12 rounded-none border-white/20 bg-black text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#FF3B30]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-white/60">
                    Email Address
                  </Label>
                  <Input
                    id="signup-email"
                    data-testid="signup-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className="h-12 rounded-none border-white/20 bg-black text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#FF3B30]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs font-bold uppercase tracking-[0.25em] text-white/60">
                    Store Location (for claim)
                  </Label>
                  <Select
                    required
                    value={form.store_location}
                    onValueChange={(v) => setForm({ ...form, store_location: v })}
                  >
                    <SelectTrigger
                      data-testid="signup-store-select"
                      className="h-12 rounded-none border-white/20 bg-black text-white focus:ring-2 focus:ring-[#FF3B30]"
                    >
                      <SelectValue placeholder="Choose your store" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-white/20 bg-[#121212] text-white">
                      {STORES.map((s) => (
                        <SelectItem key={s.slug} value={s.name} data-testid={`store-option-${s.slug}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="submit"
                  data-testid="signup-submit-button"
                  disabled={loading}
                  className="flex h-14 w-full items-center justify-center gap-3 bg-[#FF3B30] font-display text-lg uppercase tracking-wide text-white transition-colors duration-300 hover:bg-white hover:text-[#FF3B30] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
                  {loading ? "Registering..." : "Register & Claim Offer"}
                </button>
                <p className="text-xs text-white/40">
                  By registering you agree to the offer terms &amp; conditions below.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
